import { useForm, useStore } from '@tanstack/react-form';
import { z } from 'zod';
import type { FormFooterProps, FormProps } from '@/types/form/form-props';
import type { InputProps } from '@/types/form/input-props';
import { cn } from '@/lib/utils';
import { RegisterGroupInputSchema } from '../api/register-group';
import { TextInput } from '@/shared/components/ui/inputs/text-input';
import { fieldToInputProps, toFormProps } from '@/utils/shared/form';
import { LoadingButton } from '@/shared/components/ui/buttons/loading-button';
import { GroupType } from '@/types/api/group';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { Label } from '@/shared/components/ui/label';
import { InputErrors } from '@/shared/components/ui/inputs/input-errors';
import { groupLabel, groupTypeLabel } from '../utils/group-labels';
import { SelectInput } from '@/shared/components/ui/inputs/select-input';
import { Button } from '@/shared/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ReactNode, useId } from 'react';

const PARTICIPATE_LABEL = 'Vill du också sälja själv?';
const PARTICIPATE_HINT = 'Du får en egen säljlänk, precis som deltagarna.';
const GROUP_TYPE_OPTIONS = Object.values(GroupType).map((value) => ({ value, label: groupTypeLabel(value) }));

const AGE_LABEL = 'Är du 18 år eller äldre?';
const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Ja' },
  { value: 'no', label: 'Nej' },
];
const UNDERAGE_NOTE =
  'Inga problem – du kan skapa gruppen, men en vuxen ledare behöver registrera utbetalningskontot. Du kan bjuda in hen direkt efter.';

// The age answer only tailors the onboarding; it is not part of the API input
export const RegisterGroupFormSchema = RegisterGroupInputSchema.extend({
  isAdult: z.enum(['yes', 'no'], { message: 'Välj ett alternativ' }),
});

export type RegisterGroupFormValues = z.infer<typeof RegisterGroupFormSchema>;

export function RegisterGroupForm({
  onSubmit,
  className,
  Footer = RegisterGroupFormFooter,
  defaultValues,
}: FormProps<RegisterGroupFormValues>) {
  const form = useForm({
    defaultValues: {
      groupName: '',
      organizationName: '',
      groupType: GroupType.SchoolClass,
      includeMemberRole: false,
      isAdult: '',
      ...defaultValues,
    } as RegisterGroupFormValues,
    validators: { onSubmit: RegisterGroupFormSchema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  const groupType = useStore(form.store, (state) => state.values.groupType);

  return (
    <form {...toFormProps(form)} className={cn('space-y-4', className)}>
      <form.Field name="groupType">
        {(field) => <SelectInput {...fieldToInputProps(field, groupLabel(groupType))} options={GROUP_TYPE_OPTIONS} />}
      </form.Field>
      <form.Field name="groupName">
        {(field) => <TextInput {...fieldToInputProps(field, groupLabel(groupType))} />}
      </form.Field>
      <form.Field name="organizationName">
        {(field) => <TextInput {...fieldToInputProps(field, groupLabel(groupType))} />}
      </form.Field>
      <div className="rounded-md border border-input shadow-xs divide-y divide-input">
        <form.Field name="isAdult">
          {(field) => {
            const props = fieldToInputProps<string>(field, AGE_LABEL);
            return <YesNoInput {...props} hint={props.value === 'no' && UNDERAGE_NOTE} />;
          }}
        </form.Field>
        <form.Field name="includeMemberRole">
          {(field) => {
            const props = fieldToInputProps<boolean>(field, PARTICIPATE_LABEL);
            return (
              <YesNoInput
                {...props}
                value={props.value ? 'yes' : 'no'}
                onChange={(value) => props.onChange(value === 'yes')}
                hint={PARTICIPATE_HINT}
              />
            );
          }}
        </form.Field>
      </div>
      <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
        {([canSubmit, isSubmitting]) => <Footer canSubmit={canSubmit} isSubmitting={isSubmitting} />}
      </form.Subscribe>
    </form>
  );
}

function YesNoInput({ label, value, errors, onChange, hint }: InputProps<string> & { hint?: ReactNode }) {
  const id = useId();

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-center justify-between gap-4">
        <Label id={id}>{label}</Label>
        <RadioGroup aria-labelledby={id} value={value} onValueChange={onChange} className="flex gap-4">
          {YES_NO_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem value={option.value} id={`${id}-${option.value}`} />
              <Label htmlFor={`${id}-${option.value}`} className="font-normal">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {errors.length > 0 && <InputErrors errors={errors} />}
    </div>
  );
}

export function RegisterGroupFormFooter({
  canSubmit,
  isSubmitting,
  submitText = 'Skapa grupp',
  resetText = 'Avbryt',
}: FormFooterProps) {
  const navigate = useNavigate();
  return (
    <div className="flex gap-2 justify-end">
      <Button variant="outline" type="reset" onClick={() => navigate(-1)}>
        {resetText}
      </Button>
      <LoadingButton disabled={!canSubmit} isLoading={isSubmitting} type="submit">
        {submitText}
      </LoadingButton>
    </div>
  );
}
